import { useContext } from "react";
import { useLocation, useParams, useSearchParams } from "react-router";

import { useAiChatOptional } from "~/ai-chat/feature/use-ai-chat";
import type { ChatContext } from "~/ai-chat/model/chat-context";
import { getPageNameFromRoute } from "~/ai-chat/model/chat-context";
import { parseSystemBranchRouteTail } from "~/branching";
import { UserMembershipContext } from "~/contexts/UserMembershipContext";
import { useCurrentUser } from "~/user/feature/use-current-user";

function extractFiltersFromParams(searchParams: URLSearchParams): ChatContext["filters"] {
  const filters: NonNullable<ChatContext["filters"]> = {};
  const type = searchParams.get("type");
  if (type) filters.type = type;
  const status = searchParams.get("status");
  if (status) filters.status = status;
  const creator = searchParams.get("creator");
  if (creator) filters.creator = creator;
  const tag = searchParams.get("tag");
  if (tag) filters.tag = tag;
  const resourceType = searchParams.get("resourceType");
  if (resourceType) filters.resourceType = resourceType;
  const mime = searchParams.get("mime");
  if (mime) filters.mime = mime;
  const extension = searchParams.get("extension");
  if (extension) filters.extension = extension;

  return Object.keys(filters).length > 0 ? filters : undefined;
}

// Active branch. The system dashboard scopes by id (`bid/:branchId`); the
// system detail route encodes the branch name in the splat tail. Either may be
// absent (default branch), which is fine — the assistant resolves it live.
function extractBranch(
  pathname: string,
  params: Record<string, string | undefined>,
): { branchId?: string; branchName?: string } {
  const branch: { branchId?: string; branchName?: string } = {};
  if (params.branchId) {
    branch.branchId = params.branchId;
  }
  if (params.systemId && pathname.startsWith("/systems/")) {
    const splat = params["*"] ?? "";
    const { desiredBranchName } = parseSystemBranchRouteTail(splat.split("/").filter(Boolean));
    if (desiredBranchName) {
      branch.branchName = desiredBranchName;
    }
  }
  return branch;
}

export function useChatContext(): ChatContext {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const currentUser = useCurrentUser();
  const { isCustomerAdmin } = useContext(UserMembershipContext);
  const aiChat = useAiChatOptional();

  const context: ChatContext = {
    userId: currentUser.status === "ready" ? currentUser.userId : "unknown",
    userName: currentUser.status === "ready" ? currentUser.name : null,
    isCustomerAdmin,
    currentRoute: location.pathname,
    currentPage: getPageNameFromRoute(location.pathname, params),
  };

  // Resource IDs from route params
  if (params.systemId) {
    context.systemId = params.systemId;
  }

  const branch = extractBranch(location.pathname, params);
  if (branch.branchId) {
    context.branchId = branch.branchId;
  }
  if (branch.branchName) {
    context.branchName = branch.branchName;
  }
  if (params.modelId) {
    context.modelId = params.modelId;
  }
  if (params.artifactId) {
    context.artifactId = params.artifactId;
  }
  if (params.artifactVersionId) {
    context.artifactVersionId = params.artifactVersionId;
  }
  if (params.artifactRevisionId) {
    context.artifactRevisionId = params.artifactRevisionId;
  }
  if (params.jobId) {
    context.jobId = params.jobId;
  }
  if (params.id && location.pathname.includes("/change-requests/")) {
    context.changeRequestId = params.id;
  }

  // UI state from query params
  const tab = searchParams.get("tab");
  if (tab) {
    context.activeTab = tab;
  }

  const searchQuery = searchParams.get("q");
  if (searchQuery) {
    context.searchQuery = searchQuery;
  }

  const compareWithId = searchParams.get("compareWith");
  if (compareWithId) {
    context.compareWithId = compareWithId;
  }

  // Filters from query params
  const filters = extractFiltersFromParams(searchParams);
  if (filters) {
    context.filters = filters;
  }

  if (aiChat && aiChat.referencedFiles.length > 0) {
    context.referencedFiles = aiChat.referencedFiles;
  }

  return context;
}
